import { isMatchingPath, linkToString } from '@/helper/urls';
import type { PartialGlobalPopupStoryblok, PartialLinkOnlyStoryblok } from '@/types/component-types-sb';

export function isGlobalPopupPathMatch(currentPath: string, targetPaths: readonly PartialLinkOnlyStoryblok[]): boolean {
  if (targetPaths.length === 0) return false;

  return targetPaths.some((targetBlock) => {
    const rawUrl = linkToString(targetBlock.link);
    return isMatchingPath(currentPath, rawUrl);
  });
}

function isEligibleCustomEventPopup(
  popup: PartialGlobalPopupStoryblok,
  pathname: string,
  displayedPopups: readonly string[]
): boolean {
  const matchesPath = popup.display_everywhere || isGlobalPopupPathMatch(pathname, popup.target_paths);
  const wasAlreadyDisplayed = popup.display_once && displayedPopups.includes(popup._uid);

  return Boolean(
    popup.is_active &&
    popup.trigger_event === 'custom_event' &&
    popup.custom_event &&
    matchesPath &&
    !wasAlreadyDisplayed
  );
}

export function getEligibleCustomEventPopups(
  popups: readonly PartialGlobalPopupStoryblok[],
  pathname: string,
  displayedPopups: readonly string[]
): PartialGlobalPopupStoryblok[] {
  return popups.filter((popup) => isEligibleCustomEventPopup(popup, pathname, displayedPopups));
}

export function hasEligibleCustomEventPopup(
  popups: readonly PartialGlobalPopupStoryblok[],
  eventName: string,
  pathname: string,
  displayedPopups: readonly string[]
): boolean {
  return popups.some(
    (popup) => popup.custom_event === eventName && isEligibleCustomEventPopup(popup, pathname, displayedPopups)
  );
}

// Specific-path popups have priority; Storyblok list order breaks ties.
export function selectHighestPriorityPopup(
  popups: readonly PartialGlobalPopupStoryblok[]
): PartialGlobalPopupStoryblok {
  return popups.find((popup) => !popup.display_everywhere) ?? popups[0]!;
}
